import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import MonitoringConfigPanel from '@/components/RouterMonitoring/MonitoringConfigPanel';
import MonitoringCharts from '@/components/RouterMonitoring/MonitoringCharts';
import RouterReportingCharts from '@/components/RouterMonitoring/RouterReportingCharts';
import RouterBackupManagement from '@/components/RouterManagement/RouterBackupManagement';
import RouterBulkOperations from '@/components/RouterManagement/RouterBulkOperations';
import RouterScheduledScripts from '@/components/RouterManagement/RouterScheduledScripts';
import RouterFirewallEditor from '@/components/RouterManagement/RouterFirewallEditor';
import RouterLLMValidator from '@/components/RouterManagement/RouterLLMValidator';
import RouterKnowledgeBase from '@/components/RouterManagement/RouterKnowledgeBase';
import RouterInterfaceManagement, {
  NotesPanel,
  IspContractModal
} from '@/components/RouterManagement/RouterInterfaceManagement';
import RouterWizards from '@/components/RouterManagement/RouterWizards';
import RouterAccessManagement from '@/components/RouterManagement/RouterAccessManagement';
import RouterStatusBadges from '@/components/RouterManagement/RouterStatusBadges';
import BandwidthTestPanel from '@/components/RouterMonitoring/BandwidthTestPanel';
import OutageEnquiryPanel from '@/components/RouterMonitoring/OutageEnquiryPanel';
import useRouterMonitoringSignalR from '@/hooks/useRouterMonitoringSignalR';
import '@/styles/ITReportStyles.scss';
import { formatDateTime } from '@/utils/formatDate';
import { 
  FaNetworkWired, 
  FaArrowLeft, 
  FaSave, 
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
  FaShieldAlt,
  FaClock,
  FaInfoCircle,
  FaCog,
  FaChartLine,
  FaChartBar,
  FaDatabase,
  FaTasks,
  FaRobot,
  FaBook,
  FaProjectDiagram,
  FaToolbox,
  FaBrain,
  FaTachometerAlt,
  FaMagic,
  FaStickyNote,
  FaLink,
  FaSpinner,
  FaFileContract,
  FaUserLock,
  FaQuestionCircle
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * Router Detail Page
 * Displays detailed configuration and management interface for a single MikroTik router
 * Supports safe mode operations for configuration changes
 */
const RouterDetailPage = () => {
  const { id } = useParams();
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [router, setRouter] = useState(null);
  const [routerStats, setRouterStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retunneling, setRetunneling] = useState(false);
  const [safeModeActive, setSafeModeActive] = useState(false);
  const [safeModeTimer, setSafeModeTimer] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [operationsSubTab, setOperationsSubTab] = useState('backups');
  const [intelligenceSubTab, setIntelligenceSubTab] = useState('validator');
  const [networkSubTab, setNetworkSubTab] = useState('interfaces');
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [properties, setProperties] = useState([]);
  const [resourceSnapshot, setResourceSnapshot] = useState(null);

  /** Navigate to a newly created action item */
  const handleNavigateToActionItem = useCallback((actionItemId) => {
    if (actionItemId) navigate(`/action-items/${actionItemId}`);
  }, [navigate]);

  /**
   * Handle real-time availability changes from SignalR.
   * Updates the connection status banner and monitoring badge immediately,
   * then re-fetches full stats and monitoring status for accuracy.
   */
  const handleAvailabilityChanged = useCallback(async (data) => {
    const { isAvailable, error: availError } = data;

    // Immediately flip the connection status banner
    setRouterStats(prev => ({
      ...prev,
      connected: isAvailable,
      error: isAvailable ? undefined : (availError || prev?.error),
      lastChecked: new Date().toISOString()
    }));

    // Update the monitoring status badge in the header
    setMonitoringStatus(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        isAvailable,
        lastError: isAvailable ? null : (availError || prev.lastError)
      };
    });

    // Background re-fetch for full data
    try {
      const [statsRes, monRes] = await Promise.all([
        apiFetch(`/api/RouterConfig/${id}/stats`),
        apiFetch(`/api/RouterConfig/${id}/monitoring-status`)
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.data) setRouterStats(statsData.data);
      }

      if (monRes.ok) {
        const monData = await monRes.json();
        if (monData.data) setMonitoringStatus(monData.data);
      }
    } catch (catchError) {
        Sentry.captureException(catchError);
      // Instant update above keeps the UI consistent
    }
  }, [apiFetch, id]);

  /**
   * Handle real-time resource snapshot updates from SignalR.
   * Keeps the overview gauge cards (CPU, Memory, Disk, etc.) in sync
   * when any user loads the resource snapshot for this router.
   */
  const handleResourceSnapshotUpdated = useCallback((data) => {
    if (data?.snapshot) {
      setResourceSnapshot(data.snapshot);
    }
  }, []);

  /**
   * Handle real-time config changes from SignalR.
   * When another user saves the router configuration, update the local
   * state so all viewers see the latest metadata without a manual refresh.
   */
  const handleConfigChanged = useCallback((data) => {
    if (data?.config) {
      setRouter(data.config);
      setConfig(data.config);
    }
  }, []);

  /**
   * Handle real-time metric updates from SignalR.
   * Updates the overview gauge cards with the latest SNMP/ICMP metric data
   * broadcast by the monitoring background service.
   */
  const handleMetricUpdate = useCallback((data) => {
    if (!data) return;
    setResourceSnapshot(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      if (data.cpuUsage != null) updated.cpuUsage = data.cpuUsage;
      if (data.memoryUsage != null) updated.memoryUsage = data.memoryUsage;
      if (data.totalMemory != null) {
        updated.totalMemoryMb = Math.round(data.totalMemory / 1048576 * 10) / 10;
      }
      if (data.freeMemory != null) {
        updated.freeMemoryMb = Math.round(data.freeMemory / 1048576 * 10) / 10;
      }
      if (data.uptime != null) updated.uptime = data.uptime;
      return updated;
    });
  }, []);

  // SignalR: subscribe to this router's group for real-time updates
  useRouterMonitoringSignalR({
    routerId: parseInt(id),
    onAvailabilityChanged: handleAvailabilityChanged,
    onResourceSnapshotUpdated: handleResourceSnapshotUpdated,
    onConfigChanged: handleConfigChanged,
    onMetricUpdate: handleMetricUpdate,
    enabled: true
  });

  useEffect(() => {
    loadRouterDetails();
    loadProperties();
  }, [id]);

  const loadProperties = async () => {
    try {
      const res = await apiFetch('/api/property/all');
      if (res.ok) {
        const d = await res.json();
        setProperties(Array.isArray(d) ? d : (d.data || []));
      }
    } catch (err) {
        Sentry.captureException(err);
      // Silently ignore property loading errors - not critical for router detail page
      console.debug('Failed to load properties:', err);
    }
  };

  /**
   * Loads router configuration and current statistics.
   * The config is loaded first and the page rendered immediately.
   * Stats, monitoring status, and resource snapshots are fetched in the
   * background so an offline router doesn't block the page from rendering.
   */
  const loadRouterDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load router configuration
      const configResponse = await apiFetch(`/api/RouterConfig/${id}`);
      if (!configResponse.ok) {
        throw new Error('Failed to load router configuration');
      }

      const configData = await configResponse.json();
      if (!configData.success) {
        throw new Error('Router configuration not found');
      }

      setRouter(configData.data);
      setConfig(configData.data);

      // Page is ready to render — clear loading state before fetching live data
      setLoading(false);

      // Load stats, monitoring, and resource snapshot in the background.
      // These calls may be slow when routers are offline.
      const loadStats = async () => {
        try {
          const statsResponse = await apiFetch(`/api/RouterConfig/${id}/stats`);
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            setRouterStats(statsData.data);

            if (statsData.data?.identity) {
              setConfig(prev => ({ ...prev, hostname: statsData.data.identity }));
              setRouter(prev => ({ ...prev, hostname: statsData.data.identity }));
            }
          }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* background fetch — don't disrupt UI */
        }
      };

      const loadMonitoring = async () => {
        try {
          const monResponse = await apiFetch(`/api/RouterConfig/${id}/monitoring-status`);
          if (monResponse.ok) {
            const monData = await monResponse.json();
            if (monData.success && monData.data) {
              setMonitoringStatus(monData.data);
            }
          }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* background fetch */
        }
      };

      const loadSnapshot = async () => {
        try {
          const r = await apiFetch(`/api/RouterConfig/${id}/resource-snapshot`);
          if (r.ok) {
            const d = await r.json();
            if (d?.success) setResourceSnapshot(d.data);
          }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* background fetch */
        }
      };

      // Fire all background fetches in parallel
      Promise.all([loadStats(), loadMonitoring(), loadSnapshot()]).catch(() => {});
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error loading router details:', err);
      setError(err.message || 'Failed to load router details');
      setLoading(false);
    }
  };

  /**
   * Handles configuration save with automatic connectivity safeguard.
   * When connectivity-critical fields (management IP, apiPort, useSsl) change, the
   * system automatically verifies the router is reachable with the proposed settings
   * before persisting them — preventing accidental loss of access.
   */
  const handleSaveConfig = async () => {
    setSaving(true);

    try {
      // Detect if connectivity-critical fields changed from the saved state
      const ipChanged = config?.ipAddress !== router?.ipAddress;
      const portChanged = config?.apiPort !== router?.apiPort;
      const sslChanged = config?.useSsl !== router?.useSsl;
      const connectivityChanged = ipChanged || portChanged || sslChanged;

      if (connectivityChanged) {
        // Verify connectivity with proposed settings before saving
        const testRes = await apiFetch(
          `/api/RouterConfig/${id}/test-connectivity?proposedPort=${config.apiPort}&proposedUseSsl=${config.useSsl}&proposedIpAddress=${encodeURIComponent(config.ipAddress)}`,
          { method: 'POST' }
        );

        if (testRes.ok) {
          const testData = await testRes.json();
          if (!testData.success) {
            toast.error(`Connectivity check failed — save blocked. ${testData.error} The configuration was NOT saved to prevent losing access to this router.`);
            setSaving(false);
            return;
          }
        } else {
          toast.error('Unable to verify connectivity with proposed settings. Save aborted for safety.');
          setSaving(false);
          return;
        }
      }

      const response = await apiFetch('/api/RouterConfig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      const data = await response.json();
      if (data.success) {
        toast.success(connectivityChanged
          ? 'Configuration saved successfully. Connectivity verified with new settings.'
          : 'Configuration saved successfully.');
        setRouter(data.data);
        setConfig(data.data);
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error saving configuration:', err);
      toast.error(`Failed to save configuration: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handles re-tunnelling WireGuard to the new server for an already-registered router
   * (migration scenario). Pushes a fresh WireGuard interface/peer to the router via its
   * current management address, waits for a handshake, then flips the management IP to
   * the new tunnel address. Does NOT touch RADIUS/SNMP/firewall/firmware — those are
   * assumed to already be configured and are intentionally left alone.
   */
  const handleRetunnelWireGuard = async () => {
    if (!window.confirm(
      'This will push a new WireGuard tunnel to this router and, once connected, switch ' +
      "CapTrac's management IP for it to the new tunnel address. RADIUS, SNMP, firewall, " +
      'and firmware settings will not be touched. This can take up to a minute. Continue?'
    )) {
      return;
    }

    setRetunneling(true);
    try {
      const response = await apiFetch(`/api/RouterProvisioning/${id}/retunnel-wireguard`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(`WireGuard re-tunnelled successfully. New management IP: ${data.vpnIpAddress}`);
        // Quiet refresh of the router config (avoid the full-page loading spinner
        // that loadRouterDetails() would trigger via setLoading(true)).
        const refreshed = await apiFetch(`/api/RouterConfig/${id}`);
        if (refreshed.ok) {
          const refreshedData = await refreshed.json();
          if (refreshedData.success) {
            setRouter(refreshedData.data);
            setConfig(refreshedData.data);
          }
        }
      } else {
        toast.error(`Re-tunnel failed: ${data.error || 'Unknown error'}`);
      }
    } catch (err) {
      Sentry.captureException(err);
      console.error('Error re-tunnelling WireGuard:', err);
      toast.error(`Failed to re-tunnel WireGuard: ${err.message}`);
    } finally {
      setRetunneling(false);
    }
  };

  /**
   * Confirms safe mode changes to prevent rollback
   */
  const handleConfirmSafeMode = async () => {
    try {
      const response = await apiFetch(`/api/RouterConfig/${id}/confirm-safe-mode`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to confirm safe mode');
      }

      setSafeModeActive(false);
      setSafeModeTimer(null);
      alert('Safe mode confirmed. Configuration changes are now permanent. The safe mode backup has been removed.');
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error confirming safe mode:', err);
      alert(`Failed to confirm safe mode: ${err.message}`);
    }
  };

  const handleCancelSafeMode = async () => {
    if (!confirm('This will restore the router to its previous configuration and reboot it. Any changes made since safe mode was enabled will be lost. Continue?')) {
      return;
    }

    try {
      const response = await apiFetch(`/api/RouterConfig/${id}/cancel-safe-mode`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Failed to cancel safe mode');
      }

      setSafeModeActive(false);
      setSafeModeTimer(null);
      alert('Safe mode cancelled. The router is restoring its previous configuration and will reboot shortly.');
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error cancelling safe mode:', err);
      alert(`Failed to cancel safe mode: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <HexagonLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content" style={{ padding: '20px' }}>
        <div className="error-message" style={{ padding: '20px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', color: '#c33' }}>
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => navigate('/it/config-management/routers')} style={{ marginTop: '10px', padding: '8px 16px', cursor: 'pointer' }}>
            <FaArrowLeft /> Back to Routers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content" style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <button
          onClick={() => navigate('/it/config-management/routers')}
          style={{
            padding: '8px 16px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            marginBottom: '15px',
            fontSize: '0.9rem'
          }}
        >
          <FaArrowLeft /> Back to Routers
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 10px 0' }}>
              <FaNetworkWired /> {router?.displayName || 'Router Configuration'}
            </h1>
            <p style={{ color: '#666', margin: 0 }}>
              {router?.ipAddress}:{router?.apiPort} • RouterOS {router?.routerOSVersion || 'Unknown'}
            </p>
          </div>
          <RouterStatusBadges
            routerId={parseInt(id)}
            routerConfig={router}
            monitoringStatus={monitoringStatus}
            apiFetch={apiFetch}
            onNavigateToActionItem={handleNavigateToActionItem}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: '20px', borderBottom: '2px solid #dee2e6' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          {[
            { key: 'overview', label: 'Overview', icon: <FaCog /> },
            { key: 'network', label: 'Advanced', icon: <FaProjectDiagram /> },  // key kept as 'network' for backward compat with activeTab state
            { key: 'monitoring', label: 'Monitoring', icon: <FaChartLine /> },
            { key: 'reporting', label: 'Reporting', icon: <FaChartBar /> },
            { key: 'intelligence', label: 'Intelligence', icon: <FaBrain /> },
            { key: 'operations', label: 'Operations', icon: <FaToolbox /> },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: '12px 24px',
                background: activeTab === tab.key ? 'white' : '#f8f9fa',
                border: 'none',
                borderBottom: activeTab === tab.key ? '3px solid #007bff' : '3px solid transparent',
                cursor: 'pointer',
                fontWeight: activeTab === tab.key ? 'bold' : 'normal',
                color: activeTab === tab.key ? '#007bff' : '#6c757d',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Safe Mode Alert */}
      {safeModeActive && (
        <div style={{
          padding: '20px',
          background: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <FaShieldAlt size={24} color="#ff9800" />
            <h3 style={{ margin: 0 }}>Safe Mode Active</h3>
          </div>
          <p style={{ margin: '0 0 10px 0' }}>
            A backup of the router configuration was saved before changes were applied.
            The router will <strong>automatically restore the backup and reboot</strong> if not confirmed before the timer expires.
          </p>
          {safeModeTimer && (
            <p style={{ margin: '0 0 15px 0', fontSize: '1.2rem', fontWeight: 'bold', color: '#ff9800' }}>
              <FaClock /> Time remaining: {Math.floor(safeModeTimer / 60)}:{String(safeModeTimer % 60).padStart(2, '0')}
            </p>
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleConfirmSafeMode}
              style={{
                padding: '10px 20px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              <FaCheckCircle /> Confirm — Keep Changes
            </button>
            <button
              onClick={handleCancelSafeMode}
              style={{
                padding: '10px 20px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              <FaExclamationTriangle /> Revert — Restore Backup
            </button>
          </div>
        </div>
      )}

      {/* ============ OVERVIEW TAB ============ */}
      {activeTab === 'overview' && (
        <>
          {/* Connection Status */}
          {routerStats && (
            <div style={{
              padding: '15px',
              background: routerStats.connected ? '#e7f5e7' : '#fee',
              border: `2px solid ${routerStats.connected ? '#4caf50' : '#f44336'}`,
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              {routerStats.connected ? (
                <>
                  <FaCheckCircle color="#4caf50" size={24} />
                  <div>
                    <strong>Router Online</strong>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      Last checked: {formatDateTime(routerStats.lastChecked)}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <FaExclamationTriangle color="#f44336" size={24} />
                  <div>
                    <strong>Router Offline</strong>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>
                      {routerStats.error || 'Unable to connect to router'}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Resource Snapshot Gauges */}
          {resourceSnapshot && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '12px',
              marginBottom: '20px'
            }}>
              <ResourceGauge
                label="CPU"
                value={resourceSnapshot.cpuUsage}
                unit="%"
                thresholds={{ warn: 60, crit: 85 }}
              />
              <ResourceGauge
                label="Memory"
                value={resourceSnapshot.memoryUsage}
                unit="%"
                sub={resourceSnapshot.totalMemoryMb ? `${resourceSnapshot.freeMemoryMb?.toFixed(0)} / ${resourceSnapshot.totalMemoryMb?.toFixed(0)} MB free` : null}
                thresholds={{ warn: 70, crit: 90 }}
              />
              <ResourceGauge
                label="Disk"
                value={resourceSnapshot.diskUsage}
                unit="%"
                sub={resourceSnapshot.totalDiskMb ? `${resourceSnapshot.freeDiskMb?.toFixed(0)} / ${resourceSnapshot.totalDiskMb?.toFixed(0)} MB free` : null}
                thresholds={{ warn: 70, crit: 90 }}
              />
              <UptimeCard uptime={resourceSnapshot.uptime} boardName={resourceSnapshot.boardName} architecture={resourceSnapshot.architecture} />
              <QuickStatCard icon={<FaNetworkWired />} label="Interfaces" value={resourceSnapshot.interfaceCount} />
              <QuickStatCard icon={<FaDatabase />} label="DHCP Leases" value={resourceSnapshot.dhcpLeaseCount} />
              <QuickStatCard icon={<FaProjectDiagram />} label="Routes" value={resourceSnapshot.routeCount} />
              <QuickStatCard icon={<FaShieldAlt />} label="Firewall Rules" value={resourceSnapshot.firewallRuleCount} />
            </div>
          )}

          {/* Configuration Form */}
          <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
            <h2>Router Configuration</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '20px' }}>
              <div>
                <label htmlFor="displayName" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Display Name
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={config?.displayName || ''}
                  onChange={(e) => setConfig({ ...config, displayName: e.target.value })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="hostname" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Identity
                </label>
                <input
                  type="text"
                  id="hostname"
                  value={config?.hostname || ''}
                  readOnly
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', background: '#f5f5f5' }}
                />
                <small style={{ color: '#888', fontSize: '0.8rem' }}>
                  Set on the router via System &gt; Identity. Refreshed automatically each time this page loads.
                </small>
              </div>

              <div>
                <label htmlFor="ipAddress" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Management IP Address
                </label>
                <input
                  type="text"
                  id="ipAddress"
                  value={config?.ipAddress || ''}
                  onChange={(e) => setConfig({ ...config, ipAddress: e.target.value })}
                  placeholder="e.g. 10.0.11.43"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ color: '#888', fontSize: '0.8rem' }}>
                  The IP used to provision and monitor this router. Outage action items are created when this address becomes unreachable.
                </small>
              </div>

              <div>
                <label htmlFor="location" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  value={config?.location || ''}
                  onChange={(e) => setConfig({ ...config, location: e.target.value })}
                  placeholder="e.g. Server room, Main office"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="apiPort" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  API Port
                </label>
                <input
                  type="number"
                  id="apiPort"
                  value={config?.apiPort || 8728}
                  onChange={(e) => setConfig({ ...config, apiPort: parseInt(e.target.value) })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              <div>
                <label htmlFor="propertyId" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Property Assignment
                </label>
                <select
                  id="propertyId"
                  value={config?.propertyId || ''}
                  onChange={(e) => setConfig({ ...config, propertyId: e.target.value ? parseInt(e.target.value) : null })}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="">— No property assigned —</option>
                  {properties.map(p => (
                    <option key={p.propertyID || p.id} value={p.propertyID || p.id}>
                      {p.propertyName || p.name}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#888', fontSize: '0.8rem' }}>
                  Link this router to a property. Outage action items will inherit this property.
                </small>
              </div>

              <div style={{ display: 'flex', gap: '24px', alignItems: 'center', marginTop: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={config?.useSsl || false}
                    onChange={(e) => setConfig({ ...config, useSsl: e.target.checked })}
                  />
                  <span>Use SSL (API-SSL)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={config?.isEnabled || false}
                    onChange={(e) => setConfig({ ...config, isEnabled: e.target.checked })}
                  />
                  <span>Enabled for data collection</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleSaveConfig}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  background: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FaSave /> {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div style={{ padding: '15px', background: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
              <FaShieldAlt color="#2196f3" size={20} style={{ marginTop: '2px' }} />
              <div>
                <strong>Automatic Connectivity Safeguard</strong>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem' }}>
                  When you change connectivity settings (Management IP Address, API Port, or SSL), CapTrac automatically
                  verifies the router is reachable with the new settings <strong>before</strong> saving. If the connectivity
                  test fails, the save is blocked to prevent losing access to the router. Metadata-only changes (display
                  name, location, notes) are saved immediately without a connectivity check.
                </p>
              </div>
            </div>
          </div>

          {/* Server Migration: Re-tunnel WireGuard */}
          <div style={{ padding: '15px', background: '#fff8e1', border: '1px solid #f5a623', borderRadius: '8px', marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'start', gap: '10px' }}>
              <FaLink color="#f5a623" size={20} style={{ marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <strong>Server Migration: Re-tunnel WireGuard</strong>
                <p style={{ margin: '5px 0 10px 0', fontSize: '0.9rem' }}>
                  Moving this router to a new CapTrac server? This pushes a fresh WireGuard tunnel to the router
                  over its current Management IP, waits for the handshake, then switches the Management IP to the
                  new tunnel address. RADIUS, SNMP, firewall, and firmware settings are left exactly as configured —
                  use the Router Setup &amp; Management Wizard instead if you're onboarding a brand-new router.
                </p>
                <button
                  onClick={handleRetunnelWireGuard}
                  disabled={retunneling}
                  style={{
                    padding: '10px 20px',
                    background: '#f5a623',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: retunneling ? 'not-allowed' : 'pointer',
                    opacity: retunneling ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <FaSync className={retunneling ? 'fa-spin' : ''} />
                  {retunneling ? 'Re-tunnelling (this can take up to a minute)...' : 'Re-tunnel WireGuard to New Server'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ============ NETWORK TAB ============ */}
      {activeTab === 'network' && (
        <>
          {/* Network sub-tabs */}
          <div style={{
            display: 'flex',
            gap: '0',
            borderBottom: '2px solid #e9ecef',
            marginBottom: '16px'
          }}>
            {[
              { key: 'interfaces', label: 'Interfaces & Routing', icon: <FaNetworkWired /> },
              { key: 'firewall', label: 'Firewall & Security', icon: <FaShieldAlt /> },
              { key: 'access', label: 'Access Management', icon: <FaUserLock /> },
              { key: 'bandwidth', label: 'Bandwidth Testing', icon: <FaTachometerAlt /> },
              { key: 'wizards', label: 'Wizards', icon: <FaMagic /> },
            ].map(sub => (
              <button
                key={sub.key}
                onClick={() => setNetworkSubTab(sub.key)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: networkSubTab === sub.key ? '3px solid #495057' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: networkSubTab === sub.key ? '600' : 'normal',
                  color: networkSubTab === sub.key ? '#212529' : '#6c757d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
              >
                {sub.icon} {sub.label}
              </button>
            ))}
          </div>

          {networkSubTab === 'interfaces' && (
            <RouterInterfaceManagement
              routerId={parseInt(id)}
              routerConfig={router}
              onConfigUpdate={(updated) => { setRouter(updated); setConfig(updated); }}
            />
          )}

          {networkSubTab === 'firewall' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <RouterFirewallEditor
                routerId={parseInt(id)}
                routerName={router?.displayName || router?.ipAddress || 'Router'}
              />
            </div>
          )}

          {/* Access Management sub-tab: manage router users, passwords and services */}
          {networkSubTab === 'access' && (
            <RouterAccessManagement
              routerId={parseInt(id)}
              routerConfig={router}
              apiFetch={apiFetch}
            />
          )}

          {networkSubTab === 'bandwidth' && (
            <BandwidthTestPanel
              routerId={id}
              apiFetch={apiFetch}
            />
          )}

          {networkSubTab === 'wizards' && (
            <RouterWizards
              routerId={parseInt(id)}
              routerConfig={router}
            />
          )}
        </>
      )}

      {/* ============ MONITORING TAB ============ */}
      {activeTab === 'monitoring' && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <MonitoringConfigPanel 
              routerId={id}
              apiFetch={apiFetch}
              onStatusChange={setMonitoringStatus}
            />
          </div>
          <div>
            <MonitoringCharts 
              routerId={id}
              apiFetch={apiFetch}
            />
          </div>
        </>
      )}

      {/* ============ REPORTING TAB ============ */}
      {activeTab === 'reporting' && (
        <>
          <div>
            <RouterReportingCharts
              routerId={id}
              apiFetch={apiFetch}
            />
          </div>
        </>
      )}

      {/* ============ OPERATIONS TAB ============ */}
      {activeTab === 'operations' && (
        <>
          {/* Operations sub-tabs */}
          <div style={{
            display: 'flex',
            gap: '0',
            borderBottom: '2px solid #e9ecef',
            marginBottom: '16px'
          }}>
            {[
              { key: 'backups', label: 'Backups', icon: <FaDatabase /> },
              { key: 'bulk', label: 'Bulk Operations', icon: <FaTasks /> },
              { key: 'scripts', label: 'Scheduled Scripts', icon: <FaClock /> },
            ].map(sub => (
              <button
                key={sub.key}
                onClick={() => setOperationsSubTab(sub.key)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: operationsSubTab === sub.key ? '3px solid #495057' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: operationsSubTab === sub.key ? '600' : 'normal',
                  color: operationsSubTab === sub.key ? '#212529' : '#6c757d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
              >
                {sub.icon} {sub.label}
              </button>
            ))}
          </div>

          {operationsSubTab === 'backups' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <RouterBackupManagement 
                routerId={id}
                apiFetch={apiFetch}
              />
            </div>
          )}

          {operationsSubTab === 'bulk' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <RouterBulkOperations 
                routerId={parseInt(id)}
              />
            </div>
          )}

          {operationsSubTab === 'scripts' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <RouterScheduledScripts 
                routerId={parseInt(id)}
              />
            </div>
          )}
        </>
      )}

      {/* ============ INTELLIGENCE TAB ============ */}
      {activeTab === 'intelligence' && (
        <>
          {/* Intelligence sub-tabs — includes AI Review, Knowledge Base, Notes, and ISP Links */}
          <div style={{
            display: 'flex',
            gap: '0',
            borderBottom: '2px solid #e9ecef',
            marginBottom: '16px'
          }}>
            {[
              { key: 'validator', label: 'AI Review', icon: <FaRobot /> },
              { key: 'knowledge', label: 'Knowledge Base', icon: <FaBook /> },
              { key: 'outageEnquiry', label: 'Outage Enquiry', icon: <FaQuestionCircle /> },
              { key: 'notes', label: 'Notes', icon: <FaStickyNote /> },
              { key: 'ispLinks', label: 'Service Contracts', icon: <FaLink /> },
            ].map(sub => (
              <button
                key={sub.key}
                onClick={() => setIntelligenceSubTab(sub.key)}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: intelligenceSubTab === sub.key ? '3px solid #495057' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: intelligenceSubTab === sub.key ? '600' : 'normal',
                  color: intelligenceSubTab === sub.key ? '#212529' : '#6c757d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
              >
                {sub.icon} {sub.label}
              </button>
            ))}
          </div>

          {intelligenceSubTab === 'validator' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <RouterLLMValidator 
                routerId={parseInt(id)}
                routerName={router?.displayName || router?.ipAddress}
              />
            </div>
          )}

          {intelligenceSubTab === 'knowledge' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <RouterKnowledgeBase 
                routerId={parseInt(id)}
              />
            </div>
          )}

          {/* Outage Enquiry sub-tab — correlates router outages with CM Daily Checklist gaps */}
          {intelligenceSubTab === 'outageEnquiry' && (
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
              <OutageEnquiryPanel
                routerId={parseInt(id)}
                apiFetch={apiFetch}
                propertyId={router?.propertyId}
              />
            </div>
          )}

          {/* Notes sub-tab — moved here from Interfaces & Routing for better discoverability */}
          {intelligenceSubTab === 'notes' && (
            <NotesPanel
              routerConfig={router}
              onConfigUpdate={(updated) => { setRouter(updated); setConfig(updated); }}
              showSuccess={(msg) => toast.success(msg)}
              showError={(msg) => toast.error(msg)}
            />
          )}

          {/* ISP Links sub-tab — consolidated view of all ISP contracts for this router's public IPs */}
          {intelligenceSubTab === 'ispLinks' && (
            <RouterIspLinksPanel
              routerId={parseInt(id)}
              apiFetch={apiFetch}
            />
          )}
        </>
      )}
    </div>
  );
};

/**
 * Circular-style resource gauge for CPU/Memory/Disk
 */
const ResourceGauge = ({ label, value, unit = '%', sub, thresholds = {} }) => {
  const pct = value ?? 0;
  const color = pct >= (thresholds.crit || 90) ? '#d32f2f'
    : pct >= (thresholds.warn || 70) ? '#ed6c02'
    : '#2e7d32';
  const circumference = 2 * Math.PI * 36;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '14px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <svg width="80" height="80" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="36" fill="none" stroke="#eee" strokeWidth="6" />
        <circle
          cx="40" cy="40" r="36" fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 40 40)"
          style={{ transition: 'stroke-dashoffset 0.5s' }}
        />
        <text x="40" y="40" textAnchor="middle" dominantBaseline="central"
          style={{ fontSize: '16px', fontWeight: 700, fill: color }}>
          {value != null ? `${value}${unit}` : '—'}
        </text>
      </svg>
      {sub && (
        <div style={{ fontSize: '0.65rem', color: '#aaa', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
};

/**
 * Quick stat card with icon for interface/DHCP/route/firewall counts
 */
const QuickStatCard = ({ icon, label, value }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '14px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4
  }}>
    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#888', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ color: '#1976d2', fontSize: 16 }}>{icon}</span>
      <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#333' }}>{value ?? '—'}</span>
    </div>
  </div>
);

/**
 * Parses MikroTik uptime string (e.g. "2w2d19h39m59s") into structured segments.
 */
const parseUptime = (raw) => {
  if (!raw || raw === '—') return null;
  const parts = [];
  const regex = /(\d+)([wdhms])/g;
  let match;
  while ((match = regex.exec(raw)) !== null) {
    const value = parseInt(match[1], 10);
    const unit = match[2];
    const labels = { w: 'week', d: 'day', h: 'hour', m: 'min', s: 'sec' };
    const label = labels[unit] || unit;
    parts.push({ value, label: value !== 1 ? `${label}s` : label, short: unit });
  }
  return parts.length > 0 ? parts : null;
};

/**
 * Uptime card with parsed segments displayed in a clean layout
 */
const UptimeCard = ({ uptime, boardName, architecture }) => {
  const segments = parseUptime(uptime);
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '14px 16px',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <FaClock style={{ color: '#1976d2', fontSize: 13 }} />
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Uptime
        </span>
      </div>
      {segments ? (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
          {segments.map((seg, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
              <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#333', lineHeight: 1 }}>{seg.value}</span>
              <span style={{ fontSize: '0.65rem', fontWeight: 500, color: '#999', textTransform: 'lowercase' }}>{seg.label}</span>
            </span>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#333' }}>{uptime || '—'}</div>
      )}
      {boardName && (
        <div style={{ fontSize: '0.65rem', color: '#aaa', marginTop: 0, lineHeight: 1.3 }}>
          {boardName}{architecture ? ` • ${architecture}` : ''}
        </div>
      )}
    </div>
  );
};

export default RouterDetailPage;

// =============================================================================
// RouterIspLinksPanel
// =============================================================================
/**
 * ISP Links panel — displays all public IP addresses for a given router alongside
 * their associated ISP contracts. Allows opening the full IspContractModal for
 * editing directly from the Intelligence tab, providing a consolidated view of
 * all ISP relationships without having to navigate through the Network tab.
 *
 * @param {number} routerId - The router ID to load public addresses for.
 * @param {Function} apiFetch - Authenticated fetch function from AuthContext.
 */
const RouterIspLinksPanel = ({ routerId, apiFetch }) => {
  const [publicAddresses, setPublicAddresses] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ispContractTarget, setIspContractTarget] = useState(null);

  // Load public addresses and their contracts in parallel
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [addrRes, contractsRes] = await Promise.all([
        apiFetch(`/api/RouterConfig/${routerId}/public-addresses`),
        apiFetch(`/api/ServiceContracts/by-router/${routerId}`)
      ]);

      if (!addrRes.ok) throw new Error('Failed to load public addresses');
      if (!contractsRes.ok) throw new Error('Failed to load ISP contracts');

      const addrJson = await addrRes.json();
      const contractsJson = await contractsRes.json();

      setPublicAddresses(addrJson.data || []);
      setContracts(contractsJson.data || []);
    } catch (err) {
        Sentry.captureException(err);
      setError('Failed to load ISP link data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [routerId, apiFetch]);

  useEffect(() => {
    load();
  }, [load]);

  // Build a map from public address ID → contracts/terms for quick lookup
  // In the new generic model, contracts have terms that can each link to a public address
  const contractsByAddressId = {};
  for (const contract of contracts) {
    // Check if the contract itself links to a public address
    if (contract.linkedEntityType === 'RouterPublicAddress' && contract.linkedEntityId) {
      if (!contractsByAddressId[contract.linkedEntityId]) contractsByAddressId[contract.linkedEntityId] = [];
      contractsByAddressId[contract.linkedEntityId].push(contract);
    }
    // Also check individual terms
    if (contract.terms) {
      for (const term of contract.terms) {
        if (term.linkedEntityType === 'RouterPublicAddress' && term.linkedEntityId) {
          if (!contractsByAddressId[term.linkedEntityId]) contractsByAddressId[term.linkedEntityId] = [];
          if (!contractsByAddressId[term.linkedEntityId].some(c => c.serviceContractId === contract.serviceContractId)) {
            contractsByAddressId[term.linkedEntityId].push(contract);
          }
        }
      }
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
        <FaSpinner className="spin" style={{ fontSize: '1.5rem' }} />
        <p style={{ marginTop: '12px' }}>Loading service contract data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', color: '#dc3545', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107' }}>
        <FaExclamationTriangle /> {error}
      </div>
    );
  }

  if (publicAddresses.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d', background: 'white', border: '1px solid #ddd', borderRadius: '8px' }}>
        <FaLink style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.4 }} />
        <p>No public IP addresses configured for this router.</p>
        <p style={{ fontSize: '0.85rem' }}>Add public IPs in the Network → Interfaces &amp; Routing → IP Addresses tab to enable service contract linking.</p>
      </div>
    );
  }

  return (
    <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Panel header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
            <FaLink style={{ marginRight: '8px', color: '#495057' }} />
            Service Contracts — Public IP Links
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#6c757d' }}>
            Manage service contracts associated with each public IP address on this router.
          </p>
        </div>
        <span style={{
          background: contracts.length > 0 ? '#d4edda' : '#f8f9fa',
          color: contracts.length > 0 ? '#155724' : '#6c757d',
          padding: '3px 10px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 600
        }}>
          {contracts.length}/{publicAddresses.length} linked
        </span>
      </div>

      {/* Public IP rows */}
      <div style={{ padding: '8px 0' }}>
        {publicAddresses.map(address => {
          const addressContracts = contractsByAddressId[address.id] || [];
          const hasContract = addressContracts.length > 0;

          // Find the earliest expiry across all terms linked to this address
          let earliestExpiry = null;
          let earliestDays = null;
          for (const c of addressContracts) {
            const matchingTerms = (c.terms || []).filter(t => 
              (t.linkedEntityType === 'RouterPublicAddress' && t.linkedEntityId === address.id) || 
              (c.linkedEntityType === 'RouterPublicAddress' && c.linkedEntityId === address.id)
            );
            for (const t of matchingTerms.length > 0 ? matchingTerms : (c.terms || [])) {
              if (t.expiryDate) {
                const exp = new Date(t.expiryDate);
                if (!earliestExpiry || exp < earliestExpiry) {
                  earliestExpiry = exp;
                  earliestDays = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24));
                }
              }
            }
          }

          const getExpiryStyle = () => {
            if (earliestDays === null) return {};
            if (earliestDays < 0) return { color: '#dc3545', fontWeight: 600 };
            if (earliestDays <= 7) return { color: '#dc3545', fontWeight: 600 };
            if (earliestDays <= 30) return { color: '#e6a000', fontWeight: 600 };
            return { color: '#28a745' };
          };

          return (
            <div key={address.id} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 20px',
              borderBottom: '1px solid #f0f0f0',
              transition: 'background 0.15s'
            }}>
              {/* IP / label info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                    {address.label || address.ipAddress}
                  </span>
                  {address.label && (
                    <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                      {address.ipAddress}
                    </span>
                  )}
                  <span style={{
                    fontSize: '0.75rem',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    background: hasContract ? '#d4edda' : '#f8f9fa',
                    color: hasContract ? '#155724' : '#6c757d',
                    border: `1px solid ${hasContract ? '#c3e6cb' : '#dee2e6'}`
                  }}>
                    {hasContract ? `${addressContracts.length} contract(s)` : 'No contract'}
                  </span>
                </div>

                {/* Contract summaries */}
                {hasContract && addressContracts.map(contract => {
                  // Parse custom data from terms for ISP-specific fields
                  const relTerms = (contract.terms || []).filter(t =>
                    (t.linkedEntityType === 'RouterPublicAddress' && t.linkedEntityId === address.id) ||
                    (contract.linkedEntityType === 'RouterPublicAddress' && contract.linkedEntityId === address.id)
                  );
                  const displayTerms = relTerms.length > 0 ? relTerms : (contract.terms || []);

                  return (
                    <div key={contract.serviceContractId} style={{ marginTop: '6px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '0.82rem', color: '#555' }}>
                      {contract.serviceProviderName && (
                        <span>
                          <FaFileContract style={{ marginRight: '4px', color: '#007bff' }} />
                          {contract.serviceProviderName}
                          {contract.contractTypeName && (
                            <span style={{ color: '#999', marginLeft: 4 }}>({contract.contractTypeName})</span>
                          )}
                        </span>
                      )}
                      {displayTerms.map(term => {
                        let customVals = {};
                          if (term.customData) {
                              try { customVals = JSON.parse(term.customData); } catch (err) {
                                  Sentry.captureException(err);
                                  // Silently ignore contract data loading errors - not critical for router detail page
                                  console.debug('Failed to load Contract properties:', err);
                              }
                          }
                        return (
                          <span key={term.serviceContractTermId} style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                            <span style={{ fontWeight: 500 }}>{term.name}</span>
                            {(customVals.expectedDownloadMbps || customVals.expectedUploadMbps) && (
                              <span>
                                <FaTachometerAlt style={{ marginRight: '4px', color: '#28a745' }} />
                                {customVals.expectedDownloadMbps ? `↓${customVals.expectedDownloadMbps} Mbps` : ''}
                                {customVals.expectedDownloadMbps && customVals.expectedUploadMbps ? ' / ' : ''}
                                {customVals.expectedUploadMbps ? `↑${customVals.expectedUploadMbps} Mbps` : ''}
                              </span>
                            )}
                            {customVals.uptimeSlaPercent && (
                              <span>
                                <FaShieldAlt style={{ marginRight: '4px', color: '#6f42c1' }} />
                                {customVals.uptimeSlaPercent}% SLA
                              </span>
                            )}
                          </span>
                        );
                      })}
                      {earliestExpiry && (
                        <span style={getExpiryStyle()}>
                          <FaClock style={{ marginRight: '4px' }} />
                          {earliestDays < 0
                            ? `Expired ${Math.abs(earliestDays)}d ago`
                            : earliestDays === 0
                              ? 'Expires today'
                              : `Expires in ${earliestDays}d (${earliestExpiry.toLocaleDateString()})`}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Edit button */}
              <button
                onClick={() => setIspContractTarget(address)}
                style={{
                  padding: '7px 14px',
                  background: hasContract ? '#007bff' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginLeft: '16px',
                  flexShrink: 0
                }}
              >
                <FaFileContract size={12} />
                {hasContract ? 'Manage Contracts' : 'Add Contract'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Service Contract modal */}
      {ispContractTarget && (
        <IspContractModal
          publicAddress={ispContractTarget}
          onClose={() => setIspContractTarget(null)}
          onSaved={() => { setIspContractTarget(null); load(); }}
        />
      )}
    </div>
  );
};
